(function () {
  const config = window.vehicleFitmentConfig;
  if (!config || !config.enabled) {
    console.warn('Vehicle fitment: missing or disabled config');
    return;
  }

  const STORAGE_KEY = config.storageKey || 'shiftsync_vehicle';
  const PAID_ADAPTERS = new Set(['audi', 'bmw', 'vw']);
  const BUNDLE_ADAPTERS = new Set(['audi', 'bmw', 'vw', '5pk']);

  let fitmentData = null;
  let dataPromise = null;
  let popularity = null;

  // US / enthusiast popularity for manual shift knobs (higher = shown first).
  // Catalog frequency from the fitment CSV is used as a tie-breaker.
  const POPULAR_MAKES = [
    'Honda',
    'Toyota',
    'BMW',
    'Volkswagen',
    'Mazda',
    'Subaru',
    'Ford',
    'Nissan',
    'Audi',
    'Mini',
    'Acura',
    'Mitsubishi',
    'Lexus',
    'Porsche',
    'Hyundai',
    'Kia',
    'Chevrolet',
    'Scion',
    'Infiniti',
    'Mercedes-Benz',
    'Volvo',
    'Dodge',
    'Jeep',
    'Fiat',
    'Alfa Romeo',
    'Seat',
    'Holden',
    'Datsun',
    'De Tomaso',
  ];

  const makeRank = Object.create(null);
  POPULAR_MAKES.forEach((make, index) => {
    makeRank[make.toLowerCase()] = POPULAR_MAKES.length - index;
  });

  function buildPopularity(data) {
    const makes = Object.create(null);
    const models = Object.create(null);
    const tree = data?.tree || {};
    Object.keys(tree).forEach((year) => {
      const yearMakes = tree[year] || {};
      Object.keys(yearMakes).forEach((make) => {
        const yearModels = yearMakes[make] || {};
        makes[make] = (makes[make] || 0) + Object.keys(yearModels).length;
        if (!models[make]) models[make] = Object.create(null);
        Object.keys(yearModels).forEach((model) => {
          const transmissions = yearModels[model] || {};
          models[make][model] = (models[make][model] || 0) + Object.keys(transmissions).length;
        });
      });
    });
    return { makes, models };
  }

  function loadData() {
    if (fitmentData) return Promise.resolve(fitmentData);
    if (dataPromise) return dataPromise;
    dataPromise = fetch(config.dataUrl)
      .then((r) => r.json())
      .then((data) => {
        fitmentData = data;
        popularity = buildPopularity(data);
        return data;
      })
      .catch((err) => {
        console.error('Vehicle fitment data failed to load', err);
        dataPromise = null;
        return null;
      });
    return dataPromise;
  }

  function getVehicle() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function setVehicle(vehicle) {
    if (!vehicle) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(vehicle));
    }
    document.dispatchEvent(new CustomEvent('vehicle-fitment:changed', { detail: vehicle }));
    updateChip();
    updateBadge();
    updateInlineStatus();
    syncClearButtons();
  }

  function fillSelect(select, values, placeholder) {
    select.innerHTML = '';
    const first = document.createElement('option');
    first.value = '';
    first.textContent = placeholder;
    select.appendChild(first);
    values.forEach((value) => {
      const opt = document.createElement('option');
      opt.value = value;
      opt.textContent = formatTransmission(value);
      select.appendChild(opt);
    });
  }

  function formatTransmission(value) {
    if (value === 'manual') return 'Manual';
    if (value === 'automatic') return 'Automatic';
    return value;
  }

  function sortedKeys(obj) {
    return Object.keys(obj || {}).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }

  function comparePopularMakes(a, b) {
    const rankA = makeRank[a.toLowerCase()] || 0;
    const rankB = makeRank[b.toLowerCase()] || 0;
    if (rankA !== rankB) return rankB - rankA;
    const countA = popularity?.makes?.[a] || 0;
    const countB = popularity?.makes?.[b] || 0;
    if (countA !== countB) return countB - countA;
    return a.localeCompare(b, undefined, { sensitivity: 'base' });
  }

  function comparePopularModels(make, a, b) {
    const countA = popularity?.models?.[make]?.[a] || 0;
    const countB = popularity?.models?.[make]?.[b] || 0;
    if (countA !== countB) return countB - countA;
    return a.localeCompare(b, undefined, { sensitivity: 'base' });
  }

  function sortedMakesForYear(year) {
    return Object.keys(fitmentData?.tree?.[year] || {}).sort(comparePopularMakes);
  }

  function sortedModelsForYearMake(year, make) {
    return Object.keys(fitmentData?.tree?.[year]?.[make] || {}).sort((a, b) =>
      comparePopularModels(make, a, b)
    );
  }

  function sortedTransmissions(obj) {
    return Object.keys(obj || {}).sort((a, b) => {
      const order = { manual: 0, automatic: 1 };
      const rankA = order[a] ?? 50;
      const rankB = order[b] ?? 50;
      if (rankA !== rankB) return rankA - rankB;
      return a.localeCompare(b, undefined, { sensitivity: 'base' });
    });
  }

  function resetSelect(select, placeholder, disabled) {
    fillSelect(select, [], placeholder);
    select.disabled = !!disabled;
  }

  function getFormSelects(form) {
    return {
      year: form.querySelector('[data-vehicle-fitment-year]'),
      make: form.querySelector('[data-vehicle-fitment-make]'),
      model: form.querySelector('[data-vehicle-fitment-model]'),
      transmission: form.querySelector('[data-vehicle-fitment-transmission]'),
    };
  }

  async function populateYear(form) {
    const data = await loadData();
    const selects = getFormSelects(form);
    if (!data || !selects.year) return;
    const years = (data.years || []).slice().sort((a, b) => Number(b) - Number(a));
    fillSelect(selects.year, years, 'Select year');
  }

  function onMakeChange(form) {
    const selects = getFormSelects(form);
    const year = selects.year.value;
    const make = selects.make.value;
    clearTransmissionNote(form);
    resetSelect(selects.transmission, 'Select transmission', true);
    if (!year || !make || !fitmentData?.tree?.[year]?.[make]) {
      resetSelect(selects.model, 'Select model', true);
      return;
    }
    fillSelect(selects.model, sortedModelsForYearMake(year, make), 'Select model');
    selects.model.disabled = false;
  }

  function onYearChange(form) {
    const selects = getFormSelects(form);
    const year = selects.year.value;
    clearTransmissionNote(form);
    resetSelect(selects.model, 'Select model', true);
    resetSelect(selects.transmission, 'Select transmission', true);
    if (!year || !fitmentData?.tree?.[year]) {
      resetSelect(selects.make, 'Select make', true);
      return;
    }
    fillSelect(selects.make, sortedMakesForYear(year), 'Select make');
    selects.make.disabled = false;
  }

  function getTransmissionNote(form) {
    return form.querySelector('[data-vehicle-fitment-transmission-note]');
  }

  function clearTransmissionNote(form) {
    const note = getTransmissionNote(form);
    if (!note) return;
    note.hidden = true;
    note.textContent = '';
  }

  function setTransmissionNote(form, text) {
    const note = getTransmissionNote(form);
    if (!note) return;
    if (!text) {
      clearTransmissionNote(form);
      return;
    }
    note.hidden = false;
    note.textContent = text;
  }

  function onModelChange(form) {
    const selects = getFormSelects(form);
    const year = selects.year.value;
    const make = selects.make.value;
    const model = selects.model.value;
    clearTransmissionNote(form);
    if (!year || !make || !model || !fitmentData?.tree?.[year]?.[make]?.[model]) {
      resetSelect(selects.transmission, 'Select transmission', true);
      return;
    }
    const transmissions = sortedTransmissions(fitmentData.tree[year][make][model]);
    fillSelect(selects.transmission, transmissions, 'Select transmission');

    if (transmissions.length === 1) {
      selects.transmission.value = transmissions[0];
      selects.transmission.disabled = false;
      const label =
        transmissions[0] === 'manual'
          ? 'Manual only'
          : transmissions[0] === 'automatic'
            ? 'Automatic only'
            : `${formatTransmission(transmissions[0])} only`;
      setTransmissionNote(form, label);
    } else {
      selects.transmission.disabled = false;
    }
  }

  function applyVehicleToForm(form, vehicle) {
    if (!vehicle || vehicle.notListed || !vehicle.year) return;
    const selects = getFormSelects(form);
    selects.year.value = String(vehicle.year);
    onYearChange(form);
    selects.make.value = vehicle.make;
    onMakeChange(form);
    selects.model.value = vehicle.model;
    onModelChange(form);
    selects.transmission.value = vehicle.transmission;
  }

  function openModal(options) {
    const opts = options || {};
    // Manual open (chip/badge) always shows the modal so color-picker pages
    // never swallow the click by only scrolling to an inline form.
    if (opts.preferInline) {
      const inline = document.querySelector('[data-vehicle-fitment-inline]');
      if (inline) {
        inline.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }

    const modal = document.getElementById('VehicleFitmentModal');
    if (!modal) return;
    modal.hidden = false;
    document.body.classList.add('vehicle-fitment-open');
    const form = modal.querySelector('[data-vehicle-fitment-form]');
    if (!form) return;
    populateYear(form).then(() => applyVehicleToForm(form, getVehicle()));
    syncClearButtons();
  }

  function closeModal() {
    const modal = document.getElementById('VehicleFitmentModal');
    if (!modal) return;
    modal.hidden = true;
    document.body.classList.remove('vehicle-fitment-open');
  }

  function clearVehicle() {
    setVehicle(null);
    sessionStorage.removeItem('shiftsync_vehicle_skipped');
    document.querySelectorAll('[data-vehicle-fitment-form]').forEach((form) => {
      form.reset();
      const selects = getFormSelects(form);
      clearTransmissionNote(form);
      resetSelect(selects.make, 'Select make', true);
      resetSelect(selects.model, 'Select model', true);
      resetSelect(selects.transmission, 'Select transmission', true);
      populateYear(form);
    });
  }

  function syncClearButtons() {
    const hasVehicle = !!getVehicle();
    document.querySelectorAll('[data-vehicle-fitment-clear]').forEach((btn) => {
      btn.hidden = !hasVehicle;
    });
  }

  function updateChip() {
    const chip = document.getElementById('VehicleFitmentChip');
    const label = document.querySelector('[data-vehicle-fitment-chip-label]');
    const change = document.querySelector('.vehicle-fitment-chip__change');
    if (!chip || !label) return;
    chip.hidden = false;
    const vehicle = getVehicle();
    if (vehicle && !vehicle.notListed && vehicle.year && vehicle.make && vehicle.model) {
      label.textContent = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
      if (change) change.hidden = false;
      return;
    }
    label.textContent = (config.strings && config.strings.yourVehicle) || 'Your Car';
    if (change) change.hidden = true;
  }

  function getProductAdapterBrand() {
    if (!config.product) return null;
    const ids = config.adapterProductIds || {};
    const productId = Number(config.product.id);
    for (const key of ['audi', 'bmw', 'vw']) {
      if (ids[key] != null && Number(ids[key]) === productId) return key;
    }
    return null;
  }

  function brandLabel(adapterKey) {
    const labels = { audi: 'Audi', bmw: 'BMW', vw: 'Volkswagen' };
    return labels[adapterKey] || adapterKey;
  }

  function makeToBrand(make) {
    const m = String(make || '')
      .toLowerCase()
      .trim();
    if (!m) return null;
    if (m.includes('audi')) return 'audi';
    if (m.includes('bmw')) return 'bmw';
    if (m === 'vw' || m.includes('volkswagen') || m.includes('vw ')) return 'vw';
    return null;
  }

  function formatFitString(template, vars) {
    return String(template || '').replace(/\{(\w+)\}/g, (_, key) => (vars[key] != null ? vars[key] : ''));
  }

  function setBadgeActions({ showChange, adapterBrand }) {
    const changeBtn = document.querySelector('[data-vehicle-fit-badge-change]');
    const adapterLink = document.querySelector('[data-vehicle-fit-badge-adapter-link]');
    const strings = config.strings || {};

    if (changeBtn) {
      changeBtn.hidden = !showChange;
      changeBtn.textContent = strings.changeVehicle || 'Change vehicle';
    }

    if (!adapterLink) return;

    if (adapterBrand && PAID_ADAPTERS.has(adapterBrand) && config.adapterProductUrls?.[adapterBrand]) {
      adapterLink.hidden = false;
      adapterLink.href = config.adapterProductUrls[adapterBrand];
      adapterLink.textContent = formatFitString(strings.goToAdapter || 'Go to {brand} adapter', {
        brand: brandLabel(adapterBrand),
        make: brandLabel(adapterBrand),
      });
    } else {
      adapterLink.hidden = true;
      adapterLink.removeAttribute('href');
    }
  }

  async function updateBadge() {
    const badge = document.querySelector('[data-vehicle-fit-badge]');
    const status = document.querySelector('[data-vehicle-fit-badge-status]');
    if (!badge || !status) return;

    await ensureData();

    const vehicle = getVehicle();
    const strings = config.strings || {};
    badge.hidden = false;
    badge.classList.remove('vehicle-fit-badge--fits', 'vehicle-fit-badge--miss', 'vehicle-fit-badge--prompt');

    if (!vehicle) {
      badge.classList.add('vehicle-fit-badge--prompt');
      status.textContent = strings.selectVehicle || 'Select your vehicle to check fitment';
      setBadgeActions({ showChange: true, adapterBrand: null });
      return;
    }

    if (vehicle.notListed) {
      badge.classList.add('vehicle-fit-badge--miss');
      status.textContent = strings.notSupported || "Your vehicle isn't supported yet";
      setBadgeActions({ showChange: true, adapterBrand: null });
      return;
    }

    const adapterKey = lookupAdapter(vehicle);
    const productAdapterBrand = getProductAdapterBrand();
    const make = vehicle.make || '';
    const vehicleBrand = makeToBrand(make);

    // Paid adapter product pages: fit only when vehicle make + CSV adapter match this brand
    if (productAdapterBrand) {
      const fits =
        adapterKey === productAdapterBrand &&
        vehicleBrand === productAdapterBrand;

      if (fits) {
        badge.classList.add('vehicle-fit-badge--fits');
        status.textContent = formatFitString(strings.fits || 'Fits your {make}', { make });
        setBadgeActions({ showChange: true, adapterBrand: null });
        return;
      }

      badge.classList.add('vehicle-fit-badge--miss');
      status.textContent = formatFitString(strings.doesNotFitMake || "Doesn't fit your {make}", { make });

      // Wrong paid brand → link to the adapter this vehicle needs
      if (PAID_ADAPTERS.has(adapterKey)) {
        setBadgeActions({ showChange: false, adapterBrand: adapterKey });
      } else {
        // 5pk / no paid adapter needed
        setBadgeActions({ showChange: true, adapterBrand: null });
      }
      return;
    }

    // Shift knobs / other products: fit if vehicle is in the list
    const inList = findTreeNode(vehicle) != null || Boolean(vehicle.year && vehicle.make && vehicle.model);
    if (!inList) {
      badge.classList.add('vehicle-fit-badge--miss');
      status.textContent = strings.notSupported || "Your vehicle isn't supported yet";
      setBadgeActions({ showChange: true, adapterBrand: null });
      return;
    }

    badge.classList.add('vehicle-fit-badge--fits');
    if (adapterKey === '5pk') {
      status.textContent = formatFitString(
        strings.fitsWithFreeAdapter || 'Fits your {make} with free adapter set',
        { make }
      );
    } else if (PAID_ADAPTERS.has(adapterKey)) {
      const adapterInfo = config.adapterProducts?.[adapterKey];
      const price = adapterInfo?.price || '';
      status.textContent = formatFitString(
        strings.fitsWithPaidAdapter || 'Fits your {make} with {price}',
        { make, price }
      );
    } else {
      status.textContent = formatFitString(strings.fits || 'Fits your {make}', { make });
    }
    setBadgeActions({ showChange: true, adapterBrand: null });
  }

  function updateInlineStatus() {
    const status = document.querySelector('[data-vehicle-fitment-inline-status]');
    if (!status) return;
    const vehicle = getVehicle();
    const strings = config.strings || {};
    if (!vehicle) {
      status.hidden = true;
      status.textContent = '';
      return;
    }
    status.hidden = false;
    if (vehicle.notListed) {
      status.textContent = strings.notSupported || "Your vehicle isn't supported yet";
      return;
    }
    status.textContent = `${strings.selected || 'Selected'}: ${vehicle.year} ${vehicle.make} ${vehicle.model} (${formatTransmission(vehicle.transmission)})`;
  }

  function findTreeNode(vehicle) {
    if (!vehicle || !fitmentData?.tree) return null;
    const year = String(vehicle.year);
    const yearNode = fitmentData.tree[year];
    if (!yearNode) return null;

    const makeKey = Object.keys(yearNode).find((k) => k.toLowerCase() === String(vehicle.make || '').toLowerCase());
    if (!makeKey) return null;
    const makeNode = yearNode[makeKey];

    const modelKey = Object.keys(makeNode).find((k) => k.toLowerCase() === String(vehicle.model || '').toLowerCase());
    if (!modelKey) return null;
    const modelNode = makeNode[modelKey];

    const transmission = String(vehicle.transmission || '').toLowerCase();
    return modelNode?.[transmission] || null;
  }

  function lookupAdapter(vehicle) {
    if (!vehicle || vehicle.notListed) return '5pk';
    const node = findTreeNode(vehicle);
    return node || vehicle.adapter || '5pk';
  }

  function getAdapterVariantId(adapterKey) {
    if (!BUNDLE_ADAPTERS.has(adapterKey)) return null;
    const id = config.adapters?.[adapterKey];
    if (!id) {
      console.warn(
        `[ShiftSync] Adapter "${adapterKey}" needed, but no product is assigned in Theme settings → Vehicle fitment.`
      );
    }
    return id || null;
  }

  function isKnobProduct() {
    if (!config.product) return false;
    const adapterIds = Object.values(config.adapterProductIds || {})
      .filter(Boolean)
      .map(Number);
    if (adapterIds.includes(Number(config.product.id))) return false;
    const tag = (config.knobTag || '').trim().toLowerCase();
    if (!tag) return true;
    return (config.product.tags || []).some((t) => String(t).toLowerCase() === tag);
  }

  async function ensureData() {
    return loadData();
  }

  async function getBundleItems(mainVariantId, quantity) {
    await ensureData();
    if (!isKnobProduct()) return null;
    const vehicle = getVehicle();
    if (!vehicle || vehicle.notListed) return null;

    const adapterKey = lookupAdapter(vehicle);
    if (!BUNDLE_ADAPTERS.has(adapterKey)) return null;

    const adapterVariantId = getAdapterVariantId(adapterKey);
    if (!adapterVariantId) return null;

    try {
      const cart = await fetch((window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart') + '.js').then(
        (r) => r.json()
      );
      const already = (cart.items || []).some((item) => Number(item.variant_id) === Number(adapterVariantId));
      if (already) return null;
    } catch (e) {
      // continue
    }

    const shipsWith = (config.strings && config.strings.shipsWith) || 'Ships in the same box as your ShiftSync shifter';
    return [
      {
        id: Number(mainVariantId),
        quantity: Number(quantity) || 1,
      },
      {
        id: Number(adapterVariantId),
        quantity: 1,
        properties: {
          Shipping: shipsWith,
          _ships_with: 'shifter',
          _fitment_make: vehicle.make,
          _fitment_vehicle: `${vehicle.year} ${vehicle.make} ${vehicle.model}`,
          _fitment_adapter: adapterKey,
        },
      },
    ];
  }

  function schedulePopup() {
    if (config.skipAutoPopup) return;
    if (document.querySelector('[data-vehicle-fitment-inline]')) return;

    const maybeOpen = () => {
      if (getVehicle() || sessionStorage.getItem('shiftsync_vehicle_skipped') === '1') {
        return;
      }
      openModal({ preferInline: true });
    };

    const run = () => window.setTimeout(maybeOpen, 350);

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run);
    } else {
      run();
    }
  }

  function bindForm(form) {
    if (!form || form.dataset.vehicleFitmentBound === '1') return;
    form.dataset.vehicleFitmentBound = '1';

    const selects = getFormSelects(form);
    selects.year?.addEventListener('change', () => onYearChange(form));
    selects.make?.addEventListener('change', () => onMakeChange(form));
    selects.model?.addEventListener('change', () => onModelChange(form));

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      await ensureData();
      const year = form.year.value;
      const make = form.make.value;
      const model = form.model.value;
      const transmission = form.transmission.value;
      if (!year || !make || !model || !transmission) return;
      const adapter = findTreeNode({ year, make, model, transmission }) || '5pk';
      setVehicle({ year, make, model, transmission, adapter, notListed: false });
      sessionStorage.removeItem('shiftsync_vehicle_skipped');
      closeModal();
    });

    populateYear(form).then(() => applyVehicleToForm(form, getVehicle()));
  }

  function bindModal() {
    document.querySelectorAll('[data-vehicle-fitment-form]').forEach((form) => bindForm(form));

    // Event delegation survives product color/section re-renders.
    document.addEventListener('click', (e) => {
      const openEl = e.target.closest('[data-vehicle-fitment-open]');
      if (openEl) {
        e.preventDefault();
        openModal({ preferInline: false });
        return;
      }

      const skipEl = e.target.closest('[data-vehicle-fitment-skip]');
      if (skipEl) {
        e.preventDefault();
        sessionStorage.setItem('shiftsync_vehicle_skipped', '1');
        closeModal();
        return;
      }

      const clearEl = e.target.closest('[data-vehicle-fitment-clear]');
      if (clearEl) {
        e.preventDefault();
        clearVehicle();
        return;
      }

      const closeEl = e.target.closest('[data-vehicle-fitment-close]');
      if (closeEl) {
        e.preventDefault();
        closeModal();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeModal();
    });
  }

  window.ShiftSyncVehicleFitment = {
    getVehicle,
    setVehicle,
    clearVehicle,
    openModal,
    closeModal,
    getBundleItems,
    isKnobProduct,
    ensureData,
  };

  bindModal();
  updateChip();
  updateBadge();
  updateInlineStatus();
  syncClearButtons();
  loadData();
  schedulePopup();
})();
